import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { UserName } from "@/components/badges/UserName";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MessageSquare, Pin, Plus, Trash2 } from "lucide-react";
import { useDiscussions, useDeleteDiscussion, Discussion } from "@/hooks/useDiscussions";
import { useAuth } from "@/lib/auth";
import { useHasRole } from "@/hooks/useUserRoles";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { CreateDiscussionDialog } from "./CreateDiscussionDialog";
import { DiscussionThread } from "./DiscussionThread";
import { DiscussionMediaDisplay } from "./DiscussionMediaDisplay";

interface DiscussionListProps {
  courseId: string;
  moduleId?: string | null;
}

export function DiscussionList({ courseId, moduleId }: DiscussionListProps) {
  const { data: discussions, isLoading } = useDiscussions(courseId, moduleId);
  const [selectedDiscussion, setSelectedDiscussion] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  if (selectedDiscussion) {
    return (
      <DiscussionThread
        discussionId={selectedDiscussion}
        onBack={() => setSelectedDiscussion(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Discussions</h3>
        <Button onClick={() => setShowCreateDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          New Discussion
        </Button>
      </div>

      {discussions && discussions.length > 0 ? (
        <div className="space-y-3">
          {discussions.map((discussion) => (
            <DiscussionCard
              key={discussion.id}
              discussion={discussion}
              onClick={() => setSelectedDiscussion(discussion.id)}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium mb-2">No discussions yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Start a conversation about the course content
            </p>
            <Button onClick={() => setShowCreateDialog(true)} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Start Discussion
            </Button>
          </CardContent>
        </Card>
      )}

      <CreateDiscussionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        courseId={courseId}
        moduleId={moduleId}
      />
    </div>
  );
}

function DiscussionCard({
  discussion,
  onClick,
}: {
  discussion: Discussion;
  onClick: () => void;
}) {
  const { user } = useAuth();
  const isAdmin = useHasRole("admin");
  const { toast } = useToast();
  const deleteDiscussion = useDeleteDiscussion();
  const canDelete = isAdmin || user?.id === discussion.user_id;

  const handleDelete = async () => {
    try {
      await deleteDiscussion.mutateAsync({ id: discussion.id, courseId: discussion.course_id });
      toast({ title: "Discussion deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete discussion.", variant: "destructive" });
    }
  };

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          <UserAvatar avatarPath={discussion.author?.avatar_url} fullName={discussion.author?.full_name} className="h-10 w-10 flex-shrink-0" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />

          {discussion.image_url && (
            <img
              src={discussion.image_url}
              alt="Discussion attachment"
              className="h-16 w-16 object-cover rounded flex-shrink-0"
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                {discussion.is_pinned && (
                  <Pin className="h-4 w-4 text-primary" />
                )}
                <h4 className="font-medium truncate">{discussion.title}</h4>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {canDelete && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete discussion?</AlertDialogTitle>
                        <AlertDialogDescription>Are you sure you want to delete this discussion? This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <Badge variant="secondary" className="flex-shrink-0">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  {discussion.reply_count || 0}
                </Badge>
              </div>
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              {discussion.content}
            </p>

            {discussion.link_url && (
              <DiscussionMediaDisplay linkUrl={discussion.link_url} compact />
            )}

            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span><UserName userId={discussion.user_id} name={discussion.author?.full_name} /></span>
              <span>•</span>
              <span>
                {formatDistanceToNow(new Date(discussion.created_at), {
                  addSuffix: true,
                })}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
